<template>
<div class="ai-root" :style="themePageStyle">

  <nav class="ai-topbar"><div class="container d-flex align-items-center justify-content-between py-2">
    <div class="d-flex align-items-center" style="gap:12px;">
      <a href="javascript:void(0)" @click="$router.push('/')"><img src="assets/images/flockr_logo.png" alt="Flockr" style="height:34px;" /></a>
      <span class="fw-semibold" style="color:var(--fm-h,#F5F0E8);font-size:15px;">AI Vet Triage</span>
    </div>
    <div class="d-flex align-items-center gap-2">
      <button class="btn btn-sm btn-warning" @click="openVetPicker(false,'find')"><i class="mdi mdi-stethoscope me-1"></i>Find a Vet</button>
      <a href="javascript:void(0)" v-if="myAvatar || myName" @click="$router.push('/me')" title="My profile">
        <user-avatar :src="myAvatar" :name="myName" size="sm" />
      </a>
    </div>
  </div></nav>

  <div class="container ai-container">

    <!-- Hero -->
    <div class="text-center mb-4">
      <h4 class="fw-bold" style="color:var(--fm-h,#F5F0E8);">🤖 AI Vet Triage</h4>
      <p style="color:var(--fm-s,#6B6356);font-size:13px;max-width:640px;margin:0 auto;">
        The nearest vet might be 50+ km away. Describe what you're seeing — the AI will tell you what to do
        right now, what's likely wrong, and whether you need to call a vet immediately.
        <br><span style="color:#FF9800;">Not a substitute for a real vet exam — it's the gap between "something's wrong" and "the vet arrives".</span>
      </p>
    </div>

    <!-- Cap-hit banner — daily limit reached, push to vet instead -->
    <div v-if="capHit.show" class="ai-cap-banner">
      <div class="d-flex align-items-start gap-2">
        <i class="mdi mdi-clock-outline" style="font-size:24px;color:#FF9800;flex-shrink:0;"></i>
        <div style="flex:1;">
          <div style="font-weight:700;font-size:13px;color:#FF9800;">Daily AI Vet limit reached</div>
          <div style="font-size:11px;color:var(--fm-m,#A09888);margin-top:2px;">
            {{ capHit.message }} You can still talk to a real vet right now.
          </div>
          <div class="d-flex gap-2 mt-2 flex-wrap">
            <button class="btn btn-sm btn-warning" @click="openVetPicker(false,'find')"><i class="mdi mdi-stethoscope me-1"></i>Talk to a Vet Now</button>
            <button class="btn btn-sm btn-outline-light" @click="capHit.show=false"><i class="mdi mdi-close me-1"></i>Dismiss</button>
          </div>
        </div>
      </div>
    </div>

    <!-- URGENT banner — pops above the conversation when AI flags emergency -->
    <div v-if="urgent" class="ai-urgent">
      <div class="d-flex align-items-start gap-3">
        <i class="mdi mdi-alert-octagon" style="font-size:32px;color:#FF6B6B;"></i>
        <div style="flex:1;">
          <div style="font-weight:700;font-size:15px;color:#FF6B6B;">EMERGENCY — get a vet immediately</div>
          <div style="font-size:12px;color:var(--fm-m,#A09888);margin-top:4px;">
            The AI flagged this as life-threatening or fast-deteriorating. Don't wait. Call your nearest vet now or go to a livestock officer.
          </div>
          <div class="d-flex gap-2 mt-3 flex-wrap">
            <button class="btn btn-danger" @click="openVetPicker(true,'find')">
              <i class="mdi mdi-stethoscope me-1"></i>Find a Vet NOW
            </button>
            <button class="btn btn-outline-warning" @click="openVetPicker(true,'mine')">
              <i class="mdi mdi-message-text me-1"></i>Message a Vet
            </button>
            <a class="btn btn-outline-light" href="tel:+254800720555">
              <i class="mdi mdi-phone me-1"></i>Call KVB Hotline
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Initial form (only shown before first message) -->
    <div v-if="!messages.length" class="ai-form">
      <div class="row g-3">
        <div class="col-md-6">
          <label class="ai-label">Species <span style="color:#FF6B6B;">*</span></label>
          <select v-model="form.speciesId" class="form-select ai-input">
            <option value="">Select species</option>
            <option v-for="s in species" :key="s.id" :value="s.id">{{ s.name }}</option>
          </select>
        </div>
        <div class="col-md-6">
          <label class="ai-label">Animal (optional, if from your farm)</label>
          <select v-model="form.animalId" class="form-select ai-input">
            <option value="">Not linked to a specific animal</option>
            <option v-for="a in animals" :key="a.id" :value="a.id">
              {{ a.tag_number || a.name || a.id.slice(0,8) }} ({{ a.species_name || 'animal' }})
            </option>
          </select>
        </div>
        <div class="col-12">
          <label class="ai-label">What are you seeing? <span style="color:#FF6B6B;">*</span></label>
          <textarea v-model="form.symptomsText" rows="6" class="form-control ai-input" :placeholder="symptomsPlaceholder"></textarea>
          <div style="font-size:10px;color:var(--fm-s,#6B6356);margin-top:4px;">
            Include: when it started, behaviour, eating/drinking, breathing, stool, body temp if you have it, any wounds.
          </div>
        </div>

        <div class="col-12">
          <label class="ai-label">Photo (optional — auto-compressed, sent to AI for visual analysis)</label>
          <div class="ai-photo-row">
            <div v-if="form.photo" class="ai-photo-thumb">
              <img :src="form.photo.preview" />
              <button class="ai-photo-remove" @click="form.photo = null" type="button"><i class="mdi mdi-close"></i></button>
            </div>
            <label v-else-if="!compressing" class="ai-photo-add">
              <input type="file" accept="image/*" @change="onPhotoPick" />
              <i class="mdi mdi-camera-plus" style="font-size:22px;"></i>
              <span style="font-size:10px;margin-top:4px;">Add photo</span>
            </label>
            <div v-else class="ai-photo-add" style="pointer-events:none;">
              <span class="spinner-border spinner-border-sm text-warning"></span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Conversation thread -->
    <div v-if="messages.length" class="ai-chat">
      <div v-for="(m, mi) in messages" :key="mi" class="ai-msg" :class="'ai-msg-'+m.role">
        <div class="ai-msg-avatar">
          <i v-if="m.role === 'assistant'" class="mdi mdi-robot" style="color:#CE93D8;"></i>
          <i v-else class="mdi mdi-account" style="color:#BFA164;"></i>
        </div>
        <div class="ai-msg-bubble">
          <img v-if="m.photo" :src="m.photo" class="ai-msg-photo" />
          <div class="ai-msg-content" v-html="formatMessage(m.content)"></div>
        </div>
      </div>
      <div v-if="loading" class="ai-msg ai-msg-assistant">
        <div class="ai-msg-avatar"><i class="mdi mdi-robot" style="color:#CE93D8;"></i></div>
        <div class="ai-msg-bubble">
          <span class="spinner-border spinner-border-sm me-1 text-warning"></span>
          <span style="color:var(--fm-m,#A09888);">AI is analysing...</span>
        </div>
      </div>
    </div>

    <!-- Error -->
    <div v-if="error" class="text-danger" style="font-size:12px;margin-top:10px;">{{ error }}</div>

    <!-- Submit / continue -->
    <div v-if="!messages.length" class="mt-3">
      <button class="btn btn-warning btn-lg w-100" @click="submit" :disabled="loading || !form.speciesId || !form.symptomsText.trim()">
        <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
        <i v-else class="mdi mdi-robot me-1"></i>
        {{ loading ? 'Asking AI...' : 'Ask the AI Vet' }}
      </button>
    </div>

    <!-- Continuation form (after first AI response) -->
    <div v-else class="ai-followup">
      <div class="d-flex gap-2 align-items-end">
        <textarea v-model="followupText" rows="2" class="form-control ai-input" placeholder="Ask a follow-up question..." @keydown.enter.exact.prevent="sendFollowup"></textarea>
        <button class="btn btn-warning" @click="sendFollowup" :disabled="loading || !followupText.trim()">
          <i class="mdi mdi-send"></i>
        </button>
      </div>
      <div class="d-flex gap-2 mt-2 flex-wrap">
        <button class="btn btn-sm btn-outline-secondary" @click="reset">
          <i class="mdi mdi-restart me-1"></i>New triage
        </button>
        <button class="btn btn-sm btn-outline-warning" @click="$router.push('/vets')">
          <i class="mdi mdi-stethoscope me-1"></i>Find a Vet
        </button>
      </div>
      <div style="font-size:10px;color:var(--fm-s,#6B6356);text-align:center;margin-top:8px;">
        AI advice is informational only — for definitive diagnosis call a vet.
      </div>
    </div>
  </div>

  <!-- Sent confirmation toast -->
  <div v-if="sentToast.show" class="ai-sent-toast" @click="sentToast.show=false">
    <i class="mdi mdi-check-circle me-1" style="color:#50C878;"></i>
    Notified {{ sentToast.vetName }} — they were sent the AI summary by app, email{{ sentToast.smsSent ? ' and SMS' : '' }}.
    <a class="ms-2" style="color:#BFA164;text-decoration:underline;cursor:pointer;" @click.stop="$router.push(sentToast.conversationId ? ('/messages/' + sentToast.conversationId) : '/messages')">Open chat</a>
  </div>

  <vet-picker-modal v-if="showVetPicker"
    :ai-summary="lastAiSummary"
    :urgent="vetPickerUrgent"
    :animal-id="form.animalId"
    :species="speciesContext"
    :default-mode="vetPickerMode"
    @close="showVetPicker=false"
    @sent="onVetSent" />
</div>
</template>

<style>
:root{--fm-h:#F5F0E8;--fm-s:#6B6356;--fm-m:#A09888;--fm-bg1:#141414;--fm-bg2:#0F0F0F;--fm-bg3:#1E1E1E;--fm-br:#1E1E1E}
html[data-theme="light"]{--fm-h:#1A1A1A;--fm-s:#777;--fm-m:#555;--fm-bg1:#FFFFFF;--fm-bg2:#F7F5F2;--fm-bg3:#F0EBE4;--fm-br:#E8E2DA}
html[data-theme="light"] body{background:#F7F5F2!important}
html[data-theme="light"] .ai-root{background:#F7F5F2!important;color:#1A1A1A!important}
html[data-theme="light"] .ai-topbar{background:rgba(255,255,255,.95)!important;border-bottom-color:#E8E2DA!important}
html[data-theme="light"] .ai-form,
html[data-theme="light"] .ai-msg-bubble,
html[data-theme="light"] .ai-followup{background:#FFFFFF!important;border-color:#E8E2DA!important}
html[data-theme="light"] .ai-input{background:#FFFFFF!important;border-color:#E8E2DA!important;color:#1A1A1A!important}
</style>

<style scoped>
.ai-root{background:var(--fm-bg2,#0A0A0A);min-height:100vh;color:var(--fm-h,#F5F0E8)}
.ai-topbar{position:sticky;top:0;z-index:100;padding:12px 0;background:var(--fm-bg1,#141414);backdrop-filter:blur(10px);border-bottom:1px solid var(--fm-br,#1E1E1E)}
.ai-container{padding-top:80px;padding-bottom:60px;max-width:780px}
.ai-form{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:14px;padding:24px}
.ai-label{display:block;color:var(--fm-h,#F5F0E8);font-size:12px;font-weight:600;margin-bottom:6px}
.ai-input{background:var(--fm-bg2,#0F0F0F)!important;border:1px solid var(--fm-br,#1E1E1E);color:var(--fm-h,#F5F0E8)!important;font-size:13px}
.ai-input:focus{border-color:#BFA164;box-shadow:0 0 0 2px rgba(191,161,100,.15)}

.ai-urgent{background:rgba(255,107,107,.08);border:2px solid #FF6B6B;border-radius:14px;padding:18px 22px;margin-bottom:24px;animation:urgentPulse 2s ease-out infinite}
@keyframes urgentPulse{
  0%, 100% { box-shadow:0 0 0 0 rgba(255,107,107,.3); }
  50%      { box-shadow:0 0 0 12px rgba(255,107,107,0); }
}

.ai-photo-row{display:flex;gap:8px}
.ai-photo-thumb{position:relative;width:120px;height:120px;border-radius:8px;overflow:hidden;background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#1E1E1E)}
.ai-photo-thumb img{width:100%;height:100%;object-fit:cover}
.ai-photo-remove{position:absolute;top:4px;right:4px;width:24px;height:24px;border-radius:50%;background:rgba(0,0,0,.7);border:none;color:#FF6B6B;font-size:14px;cursor:pointer;display:flex;align-items:center;justify-content:center}
.ai-photo-add{width:120px;height:120px;border-radius:8px;border:2px dashed var(--fm-br,#1E1E1E);background:var(--fm-bg2,#0F0F0F);display:flex;flex-direction:column;align-items:center;justify-content:center;cursor:pointer;color:var(--fm-s,#6B6356)}
.ai-photo-add:hover{border-color:#BFA164;color:#BFA164}
.ai-photo-add input{display:none}

.ai-chat{margin-bottom:18px}
.ai-msg{display:flex;gap:10px;margin-bottom:14px}
.ai-msg-user{flex-direction:row-reverse}
.ai-msg-avatar{width:36px;height:36px;border-radius:50%;background:var(--fm-bg3,#1E1E1E);display:flex;align-items:center;justify-content:center;font-size:18px;flex-shrink:0}
.ai-msg-bubble{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:14px;padding:14px 16px;max-width:80%;font-size:13px;line-height:1.6;color:var(--fm-h,#F5F0E8)}
.ai-msg-user .ai-msg-bubble{background:rgba(191,161,100,.08);border-color:rgba(191,161,100,.3)}
.ai-msg-photo{max-width:200px;border-radius:8px;margin-bottom:8px;display:block}
.ai-msg-content{white-space:pre-wrap;word-break:break-word}
.ai-msg-content :deep(strong){color:#BFA164}

.ai-followup{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:12px;padding:14px;margin-top:14px}
.ai-cap-banner{background:rgba(255,152,0,.08);border:1px solid #FF9800;border-radius:14px;padding:14px 18px;margin-bottom:18px}
.ai-sent-toast{position:fixed;left:50%;bottom:24px;transform:translateX(-50%);background:#1E1E1E;border:1px solid #50C878;color:#F5F0E8;padding:10px 16px;border-radius:10px;font-size:12px;z-index:9998;max-width:90vw;cursor:pointer;box-shadow:0 6px 24px rgba(0,0,0,.4);}
html[data-theme="light"] .ai-sent-toast{background:#FFFFFF;color:#1A1A1A;}
</style>

<script>
/* eslint-disable */
import { executeJson, flocker } from "@/api";
import VetPickerModal from "@/components/flockr-vet/VetPickerModal.vue";
import UserAvatar from "@/components/flockr-users/UserAvatar.vue";

function compressImage(file, maxDim, quality){
  return new Promise(function(resolve){
    var reader = new FileReader();
    reader.onload = function(e){
      var img = new Image();
      img.onload = function(){
        var w = img.width, h = img.height;
        if (w > maxDim || h > maxDim) {
          if (w > h) { h = Math.round(h * maxDim / w); w = maxDim; }
          else       { w = Math.round(w * maxDim / h); h = maxDim; }
        }
        var canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        // Get base64 directly for sending to Claude
        var dataUrl = canvas.toDataURL('image/jpeg', quality);
        var b64 = dataUrl.split(',')[1] || '';
        resolve({ preview: dataUrl, base64: b64, mediaType: 'image/jpeg' });
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

export default {
  name: "AISymptomCheck",
  components: { VetPickerModal, UserAvatar },
  data(){return{
    darkMode:(document.documentElement.getAttribute('data-theme')||localStorage.getItem('flockr_theme')||'dark')!=='light',
    themeObserver:null,
    species:[], animals:[],
    form:{ speciesId:'', animalId:'', symptomsText:'', photo:null },
    compressing:false, loading:false, error:'',
    messages:[],          // {role:'user'|'assistant', content, photo?}
    followupText:'',
    urgent:false,
    showVetPicker:false, vetPickerUrgent:false, vetPickerMode:'mine',
    sentToast:{show:false,vetName:'',smsSent:false},
    capHit:{show:false,message:'',resetHours:0},
    myAvatar: sessionStorage.getItem('userAvatar') || '',
    myName: sessionStorage.getItem('userName') || '',
    symptomsPlaceholder:'e.g., One of my Friesian cows has been off feed for 2 days. She is breathing fast, ears droopy, milk dropped from 18L to 5L. No diarrhoea but coughing. Started after rain on Tuesday.'
  }},
  computed:{
    lastAiSummary(){
      for (var i = this.messages.length - 1; i >= 0; i--) {
        if (this.messages[i].role === 'assistant') return this.messages[i].content;
      }
      return '';
    },
    themePageStyle(){return this.darkMode?'background:#0A0A0A;min-height:100vh;color:#F5F0E8;':'background:#F7F5F2;min-height:100vh;color:#1A1A1A;'},
    speciesContext(){
      var s = this.species.find(x => String(x.id) === String(this.form.speciesId));
      return s ? s.name : 'an animal';
    }
  },
  mounted(){
    this._applyThemeOnce();
    var self=this;
    this.themeObserver=new MutationObserver(function(){try{self._applyThemeOnce();}catch(e){}});
    this.themeObserver.observe(document.documentElement,{attributes:true,attributeFilter:['data-theme']});
    if(!sessionStorage.getItem('userId')){this.$router.push('/login?redirect=/vet/ai-check');return}
    this.loadReference();
  },
  beforeUnmount(){if(this.themeObserver){this.themeObserver.disconnect();this.themeObserver=null}},
  methods:{
    _applyThemeOnce(){
      var current=document.documentElement.getAttribute('data-theme');
      var desired=(localStorage.getItem('flockr_theme')||current||'dark');
      if(current!==desired) document.documentElement.setAttribute('data-theme',desired);
      this.darkMode=desired!=='light';
    },
    async loadReference(){
      try{
        var sp=await executeJson({function:'getAnimalSpecies'},flocker);
        if(sp.data&&sp.data.success) this.species=(sp.data.data&&sp.data.data.species)||sp.data.data||[];
        var userId = sessionStorage.getItem('userId');
        var fr=await executeJson({function:'getMyFarms',ownerId:userId},flocker);
        var farms=(fr.data&&fr.data.success&&fr.data.data.farms)?fr.data.data.farms:[];
        var all=[];
        for(var i=0;i<farms.length;i++){
          try{
            var ar=await executeJson({function:'getFarmAnimals',farmId:farms[i].id},flocker);
            if(ar.data&&ar.data.success) all=all.concat(ar.data.data.animals||[]);
          }catch(e){}
        }
        this.animals=all;
      }catch(e){}
    },
    async onPhotoPick(event){
      var f=event.target.files&&event.target.files[0];
      event.target.value='';
      if(!f||f.type.indexOf('image/')!==0)return;
      this.compressing=true;
      try{
        var c=await compressImage(f,1200,0.8);
        this.form.photo=c;
      }catch(e){this.error='Could not process photo'}
      this.compressing=false;
    },
    formatMessage(text){
      if (!text) return '';
      var s = text.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
      // Headings (process before bold so ## is matched first)
      s = s.replace(/^###\s+(.+)$/gm, '<div style="font-weight:700;font-size:13px;color:#BFA164;margin:10px 0 4px;">$1</div>');
      s = s.replace(/^##\s+(.+)$/gm, '<div style="font-weight:700;font-size:14px;color:#BFA164;margin:12px 0 6px;text-transform:uppercase;letter-spacing:0.4px;">$1</div>');
      s = s.replace(/^#\s+(.+)$/gm, '<div style="font-weight:700;font-size:15px;color:#BFA164;margin:12px 0 6px;">$1</div>');
      // Bold and italic
      s = s.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      s = s.replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, '$1<em>$2</em>');
      // Lists
      s = s.replace(/^\s*(\d+)\.\s+/gm, '<span style="color:#BFA164;font-weight:600;">$1.</span> ');
      s = s.replace(/^\s{3,}[-•]\s+/gm, '&nbsp;&nbsp;&nbsp;<span style="color:#6B6356;">◦</span> ');
      s = s.replace(/^[-•]\s+/gm, '<span style="color:#BFA164;">•</span> ');
      // Inline code
      s = s.replace(/`([^`]+)`/g, '<code style="background:rgba(191,161,100,0.12);padding:1px 4px;border-radius:3px;">$1</code>');
      // Paragraph and line breaks
      s = s.replace(/\n\n+/g, '<br><br>');
      s = s.replace(/\n/g, '<br>');
      return s;
    },
    openVetPicker(isUrgent, mode){
      this.vetPickerUrgent = !!isUrgent;
      this.vetPickerMode = mode || 'mine';
      this.showVetPicker = true;
    },
    onVetSent(payload){
      this.showVetPicker = false;
      this.sentToast = { show:true, vetName: payload.vetName || 'the vet', smsSent: !!payload.smsSent, conversationId: payload.conversationId || '' };
      var self = this;
      setTimeout(function(){ self.sentToast.show = false; }, 6000);
    },
    detectUrgency(text){
      // Scan Claude's response for emergency markers — if any hit, flip the urgent banner.
      // Cheap and reliable; no schema change needed on the server prompt.
      if (!text) return false;
      var lower = text.toLowerCase();
      var emergencyPhrases = [
        'emergency','immediately','right now','do not wait',"don't wait",'urgent',
        'call a vet now','call the vet now','seek immediate','life-threatening',
        'critical','severe','rapid deterioration','within hours','as soon as possible',
        'go to the vet','take to the vet'
      ];
      for (var i=0;i<emergencyPhrases.length;i++){
        if (lower.indexOf(emergencyPhrases[i]) > -1) return true;
      }
      return false;
    },
    async submit(){
      if(this.loading)return;
      // First message — kick off the conversation
      var sp = this.species.find(x => String(x.id) === String(this.form.speciesId));
      var prefix = sp ? '[' + sp.name + '] ' : '';
      var question = prefix + this.form.symptomsText.trim();
      var userMsg = { role:'user', content:question };
      if (this.form.photo) userMsg.photo = this.form.photo.preview;
      this.messages.push(userMsg);
      await this.callAi(question, this.form.photo);
    },
    async sendFollowup(){
      if(this.loading||!this.followupText.trim())return;
      var q = this.followupText.trim();
      this.messages.push({ role:'user', content:q });
      this.followupText = '';
      await this.callAi(q, null);
    },
    async callAi(question, photoData){
      this.loading=true; this.error='';
      try{
        // Send prior messages as chat history (everything except the just-pushed user message)
        var history = this.messages.slice(0, -1).map(function(m){
          return { role: m.role, content: m.content };
        });
        var params = {
          function:'aiVetAssist',
          farmId: '',
          animalId: this.form.animalId || '',
          batchId: '',
          question: question,
          chatHistory: JSON.stringify(history),
          userId: sessionStorage.getItem('userId')
        };
        if (photoData) {
          params.imageBase64 = photoData.base64;
          params.imageMediaType = photoData.mediaType;
        }
        var r = await executeJson(params, flocker);
        if (r.data && r.data.success && r.data.data && r.data.data.response) {
          var resp = r.data.data.response;
          this.messages.push({ role:'assistant', content: resp });
          if (this.detectUrgency(resp)) {
            this.urgent = true;
            // Scroll to top to make the banner unmissable
            this.$nextTick(()=>window.scrollTo({top:0,behavior:'smooth'}));
          }
        } else if (r.data && r.data.data && r.data.data.code === 'limit_reached') {
          this.capHit = { show:true, message: r.data.message || 'Daily AI Vet limit reached.', resetHours: r.data.data.reset_in_hours || 24 };
        } else {
          this.error = (r.data && r.data.message) || 'Could not get AI response';
        }
      }catch(e){
        this.error=(e&&e.message)||'Network error';
      }
      this.loading=false;
    },
    reset(){
      this.messages = [];
      this.urgent = false;
      this.followupText = '';
      this.form = { speciesId:this.form.speciesId, animalId:this.form.animalId, symptomsText:'', photo:null };
      this.error = '';
    }
  }
};
</script>
